/**
 * Mappings for normalizing keyboard event keys to their base values.
 * These handle platform-specific character transformations.
 */

const DIGIT_CODE_REGEX = /^Digit([0-9])$/;
const NUMPAD_CODE_REGEX = /^Numpad([0-9])$/;

/**
 * Maps shifted number symbols back to their base number keys.
 * When Shift is held, browsers report the shifted character (e.g., '!' for Shift+1).
 * This mapping allows us to normalize back to the base key for shortcut matching.
 */
export const SHIFTED_TO_BASE: Record<string, string> = {
  '!': '1',
  '@': '2',
  '#': '3',
  $: '4',
  '%': '5',
  '^': '6',
  '&': '7',
  '*': '8',
  '(': '9',
  ')': '0',
};

/**
 * Maps macOS Option+number special characters back to their base number keys.
 * When Alt/Option is held on macOS, browsers report special characters instead
 * of the number key (e.g., '¡' for Option+1).
 */
export const ALT_TO_BASE: Record<string, string> = {
  '\u00A1': '1', // ¡
  '\u2122': '2', // ™
  '\u00A3': '3', // £
  '\u00A2': '4', // ¢
  '\u221E': '5', // ∞
  '\u00A7': '6', // §
  '\u00B6': '7', // ¶
  '\u2022': '8', // •
  '\u00AA': '9', // ª
  '\u00BA': '0', // º
};

/**
 * Normalizes a keyboard event key to a consistent format for shortcut matching.
 *
 * - Uppercase single letter keys for consistency
 * - Maps shifted symbols back to base keys when Shift is held
 * - Maps macOS Option+number characters back to base keys when Alt is held
 *
 * @param event - The keyboard event to normalize
 * @returns The normalized key string
 */
export function normalizeKeyFromEvent(event: KeyboardEvent): string {
  // Handle modifier keys - return as-is
  if (
    event.key === 'Meta' ||
    event.key === 'Alt' ||
    event.key === 'Control' ||
    event.key === 'Shift'
  ) {
    return event.key;
  }

  const code = event.code || '';

  const digitCodeMatch = code.match(DIGIT_CODE_REGEX);
  if (digitCodeMatch) {
    return digitCodeMatch[1];
  }

  const numpadCodeMatch = code.match(NUMPAD_CODE_REGEX);
  if (numpadCodeMatch) {
    return numpadCodeMatch[1];
  }

  // Normalize physical backslash key
  if (code === 'Backslash') {
    return '\\';
  }

  // Uppercase single letter keys for consistency
  if (event.key.length === 1 && /[a-z]/i.test(event.key)) {
    return event.key.toUpperCase();
  }

  // When Shift is held, map shifted symbols back to their base keys
  // This ensures Shift+1 is captured as '1' instead of '!'
  if (event.shiftKey && SHIFTED_TO_BASE[event.key]) {
    return SHIFTED_TO_BASE[event.key];
  }

  // When Alt/Option is held, map special characters back to their base keys
  // This ensures Alt+1 is captured as '1' instead of '¡'
  if (event.altKey && ALT_TO_BASE[event.key]) {
    return ALT_TO_BASE[event.key];
  }

  // All other keys (arrows, function keys, numbers, punctuation, special keys)
  return event.key;
}
